/**
 * Money, numbers and times always go through Intl with the store's locale and currency from configuration.
 * VND has no minor units; the symbol is shown only on totals and the payment screen.
 */
export function formatMoney(amount: number, locale: string, currency: string, options: { withSymbol?: boolean } = {}) {
  if (options.withSymbol) {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  }
  const digits = new Intl.NumberFormat(locale, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
  return new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(amount);
}

export function formatNumber(value: number, locale: string) {
  return new Intl.NumberFormat(locale).format(value);
}

export function formatTime(value: Date | string, locale: string, timeZone: string, hour12 = false) {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12, timeZone }).format(new Date(value));
}

/** A calendar date without a time, such as a business date ("2026-10-04"), in the locale's order. */
export function formatDate(value: string, locale: string) {
  // A date-only value is a calendar day, not an instant: read and write it in UTC so no time zone moves it.
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(value),
  );
}

/** Date and time together, e.g. the POS status bar: 24-hour clock, numeric date in the locale's order. */
export function formatDateTime(value: Date | string, locale: string, timeZone: string, hour12 = false) {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12,
    timeZone,
  }).format(new Date(value));
}
